import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import { ConfigGuard } from '@occc-app/App/guards';
import { Can } from '@occc-app/App/PolicyBasedAccessControl/components/Can';
import { ReadRetailerNetworks } from '@occc-app/App/PolicyBasedAccessControl/userActions';
import UnauthorizedPage from '@occc-app/routes/Unauthorized';
import { LoadingOverlay } from '@occc/ui-design-system';

import { useRetailerNetworksForUser } from '../hooks/use-retailer-networks-for-user.hook';
import { routeCreator } from '../retailer-connect.utils';
import { RetailerNetworkViewGuard } from '../guards/retailer-network-view.guard';
import { RetailerNetworkErrorMessage } from '../components/retailer-network-error-message.component';
import RetailerNetworkNoNetworksPage from '../retailer-network-no-networks-page';

type RetailerNetworkListPageGuardProps = {
  children: ReactNode;
};

export const RetailerNetworkListPageGuard = ({ children }: RetailerNetworkListPageGuardProps) => {
  const { data, loading, error } = useRetailerNetworksForUser(2);

  const retailerNetworkUuid = data?.retailerNetworksForUser?.edges?.[0]?.node?.uuid;

  if (loading && !data) {
    return <LoadingOverlay loaderSize={120} />;
  }

  if (error) {
    return <RetailerNetworkErrorMessage />;
  }

  if (!data || !retailerNetworkUuid) {
    return <RetailerNetworkNoNetworksPage />;
  }

  if (data?.retailerNetworksForUser?.edges?.length === 1) {
    // If the user is associated with only one retailer network, redirect to that network's overview page
    return <Navigate to={routeCreator.overview(retailerNetworkUuid)} />;
  }

  return (
    <ConfigGuard isFeatureEnabled={(config) => !!config.retailerConnect?.enabled}>
      <ConfigGuard
        isFeatureEnabled={(config) => !!config.retailerConnect?.networkManagementEnabled}
        // If network management is disabled, redirect to the overview of the first retailer network
        fallback={<Navigate to={routeCreator.overview(retailerNetworkUuid)} />}
      >
        <RetailerNetworkViewGuard>
          <Can I={ReadRetailerNetworks()} fallback={<UnauthorizedPage />}>
            {children}
          </Can>
        </RetailerNetworkViewGuard>
      </ConfigGuard>
    </ConfigGuard>
  );
};
